import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertCircle, CheckCircle2, Send, ArrowRight } from 'lucide-react';
import { submitPR } from '@/lib/api';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const SubmitPressRelease = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    headline: '',
    pressRelease: '',
    image: '',
    code: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await submitPR(formData);
      toast.success('Thank you for submitting your press release! We\'ll review it shortly.');
      setFormData({
        name: '',
        email: '',
        company: '',
        headline: '',
        pressRelease: '',
        image: '',
        code: ''
      });
    } catch (error) {
      console.error('Error submitting press release:', error);
      toast.error('Failed to submit the press release. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const guidelines = [
    { text: 'Authenticity only: Press releases that are repetitive, automated, or generated through bulk submission software will be rejected.' },
    { text: 'Restricted categories: Content related to adult material, entertainment of explicit nature, pharmaceutical pills, nutritional supplements, or spam-like promotions will not be accepted.' },
    { text: 'Copyright compliance: Submit only content and images you own or have the legal right to use.' },
    { text: 'Link policy: A maximum of 5 in-text backlinks is permitted per release.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Submit Press Release"
        description="Submit your press release for distribution to thousands of media outlets, journalists, and search engines. Fast review and publishing by Ixoraly."
        path="/submit-press-release"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <FileText className="w-8 h-8 text-coral-500" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="submit-pr-heading">
              Submit Your Press Release
            </h1>
            <p className="text-xl text-gray-300">
              Share your story with the world. Submit your press release and we'll handle the distribution to thousands of media outlets.
            </p>
          </div>
        </div>
      </section>

      {/* Submission Guidelines */}
      <section className="py-12 bg-navy-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertCircle className="text-navy-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Submission Guidelines</h2>
              <div className="space-y-3">
                {guidelines.map((guideline, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-gray-700 text-sm">{guideline.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    data-testid="pr-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full py-3"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="pr-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="w-full py-3"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Agency *
                </label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  data-testid="pr-company-input"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Your company or agency name"
                  className="w-full py-3"
                />
              </div>

              <div>
                <label htmlFor="headline" className="block text-sm font-medium text-gray-700 mb-2">
                  Headline *
                </label>
                <Input
                  id="headline"
                  name="headline"
                  type="text"
                  data-testid="pr-headline-input"
                  value={formData.headline}
                  onChange={handleChange}
                  required
                  placeholder="Your press release headline"
                  className="w-full py-3"
                />
              </div>

              <div>
                <label htmlFor="pressRelease" className="block text-sm font-medium text-gray-700 mb-2">
                  Press Release Content *
                </label>
                <Textarea
                  id="pressRelease"
                  name="pressRelease"
                  data-testid="pr-content-input"
                  value={formData.pressRelease}
                  onChange={handleChange}
                  required
                  rows={12}
                  placeholder="Enter your full press release content..."
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL (optional)
                </label>
                <Input
                  id="image"
                  name="image"
                  type="url"
                  data-testid="pr-image-input"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full py-3"
                />
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Code (optional)
                </label>
                <Input
                  id="code"
                  name="code"
                  type="text"
                  data-testid="pr-code-input"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="Enter your purchase code if you have one"
                  className="w-full py-3"
                />
              </div>

              <Button
                type="submit"
                data-testid="pr-submit-button"
                disabled={loading}
                className="w-full bg-navy-600 hover:bg-navy-700 text-white py-4 text-base font-semibold"
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    Submit Press Release
                    <Send size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 p-6 bg-navy-50 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Having trouble uploading?</h3>
              <p className="text-gray-600 text-sm mb-2">
                You can simply email us your press release along with your purchase code, and our team will handle the entire upload process for you.
              </p>
              <a href="mailto:contact@ixoraly.com" className="text-navy-600 hover:text-navy-700 font-medium text-sm">
                contact@ixoraly.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Don't have a plan yet?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Check out our affordable pricing packages and choose the one that fits your needs.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center px-8 py-4 bg-navy-600 text-white rounded-lg font-semibold hover:bg-navy-700 transition-colors"
          >
            View Pricing Plans
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SubmitPressRelease;
